/**
 * Unity Folder-Based C# Template Renderer
 * Creates nested static classes based on folder hierarchy
 */

import { ExportTemplate, NamingConvention, TemplateField } from '@/types/exportTemplates';
import type { StringsItem } from '@/lib/stores/stringsStore';
import { applyNamingConvention } from '../../core/naming';
import { LookupContext } from '../../fieldMapping/references';
import { buildFolderBasedStructure, FolderNode, ItemFieldsNode } from '../../structure/folderBased';
import { stripHtmlTags } from '@/lib/utils/documentExportUtils';

/**
 * Render folder-based C# template (Unity)
 * Creates nested static classes based on folder hierarchy
 * 
 * @param items - Items to export
 * @param template - Export template configuration
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param types - Optional types data for reference resolution
 * @param tags - Optional tags data for reference resolution
 * @param allItemsForLookup - Optional all items for glossaryItem lookups
 * @param namingConventionOverride - Optional override for all naming conventions (e.g., 'original', 'camelCase')
 * @param customNamespace - Optional custom namespace (default: 'Grimoire.Data')
 * @param customClassName - Optional custom name for the root static class (default: 'Database')
 * @param stringsItems - Optional translation strings data
 * @param dialogs - Optional dialogs array for Database.Dialogs export
 * @param renderDialogsDatabaseTemplateFn - Optional function to render dialogs (injected to avoid circular deps)
 * @param documents - Optional documents array for document field resolution
 */
export function renderFolderBasedCSharpTemplate(
  items: any[],
  template: ExportTemplate,
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[],
  namingConventionOverride?: NamingConvention,
  customNamespace?: string,
  customClassName?: string,
  stringsItems?: StringsItem[],
  dialogs?: any[],
  renderDialogsDatabaseTemplateFn?: (
    dialogs: any[],
    folderPathResolver: (folderId: string | null | undefined) => string,
    stringsMap: Map<string, StringsItem>,
    namingConvention: NamingConvention,
    indent?: number
  ) => string,
  documents?: any[],
  gameLanguages?: string[]
): string {
  const lookupContext: LookupContext = {
    types,
    tags,
    dialogs: dialogs,
    allItems: allItemsForLookup || items,
    documents: documents
  };
  
  // When only one language is configured, suppress translation keys and export as plain strings
  const useTranslationKeys = (gameLanguages?.length || 1) > 1;
  
  // Create a map of string_id to StringsItem for quick lookup
  // When single-language mode, keep stringsMap empty so translation lookups fall through to plain strings
  const stringsMap = new Map<string, StringsItem>();
  if (stringsItems && useTranslationKeys) {
    stringsItems.forEach(stringItem => {
      stringsMap.set(stringItem.id, stringItem);
    });
  }

  // Use override if provided, otherwise use template convention
  const fieldConvention = namingConventionOverride || template.namingConventions.fieldName;
  
  // Use custom namespace and class name or defaults
  const namespace = customNamespace || 'Grimoire.Data';
  const className = customClassName || 'Database';
  
  const structure = buildFolderBasedStructure(items, template, blueprints, folderPathResolver, lookupContext, namingConventionOverride, useTranslationKeys);
  
  // Helper to get C# type from mapped type string AND actual value
  const getCSharpType = (mappedType: string, actualValue?: any, isTranslatable: boolean = false, field?: TemplateField): string => {
    // Check if this is a reference field
    if (field?.isReference && field?.referenceType) {
      if (field.referenceType === 'types') {
        return Array.isArray(actualValue) ? 'TypeElement[]' : 'TypeElement';
      }
      if (field.referenceType === 'tags') {
        return Array.isArray(actualValue) ? 'TagElement[]' : 'TagElement';
      }
      if (field.referenceType === 'dialog') {
        // Dialog references store dialog keys as strings
        return Array.isArray(actualValue) ? 'string[]' : 'string';
      }
      if (field.referenceType === 'document') {
        // Document fields are exported as arrays of DocumentBlock objects
        return 'DocumentBlock[]';
      }
    }
    
    // If translatable, use TranslationKey instead of string
    if (isTranslatable) {
      if (Array.isArray(actualValue)) return 'TranslationKey[]';
      return 'TranslationKey';
    }
    
    // First check the actual value - if it's an array, return string[]
    if (Array.isArray(actualValue)) return 'string[]';
    
    // Then check mapped type
    if (mappedType.includes('bool')) return 'bool';
    if (mappedType.includes('int')) {
      // If the actual value has decimals, use double instead of int
      if (typeof actualValue === 'number' && !Number.isInteger(actualValue)) return 'double';
      return 'int';
    }
    if (mappedType.includes('float')) return 'float';
    if (mappedType.includes('double')) return 'double';
    if (mappedType.includes('[]') || mappedType.includes('Array')) return 'string[]';
    return 'string';
  };
  
  // Helper to format value for C#
  const formatValueCSharp = (value: any, mappedType: string, field?: TemplateField): string => {
    // Handle reference fields - generate object references
    if (field?.isReference && field?.referenceData && field?.referenceType) {
      console.log(`🎯 formatValueCSharp: Reference field detected - type=${field.referenceType}`);
      
      if (field.referenceType === 'types') {
        // For types, generate Variables class references for type-safe access
        if (Array.isArray(field.referenceData)) {
          // Array of type references
          const references = field.referenceData
            .map(refObj => {
              const parentTypeName = refObj._parentTypeName || 'UnknownType';
              const elementName = refObj.name || refObj.id || 'Unknown';
              const safeTypeName = parentTypeName.replace(/[^a-zA-Z0-9_]/g, '_');
              const safeElementName = elementName.replace(/[^a-zA-Z0-9_]/g, '_');
              return `Variables.${safeTypeName}.${safeElementName}`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of Variable references: new VariableElement[] { ${references} }`);
          return `new VariableElement[] { ${references} }`;
        } else {
          // Single type reference - generate Variables.TypeName.ElementName
          const parentTypeName = field.referenceData._parentTypeName || 'UnknownType';
          const elementName = field.referenceData.name || field.referenceData.id || 'Unknown';
          const safeTypeName = parentTypeName.replace(/[^a-zA-Z0-9_]/g, '_');
          const safeElementName = elementName.replace(/[^a-zA-Z0-9_]/g, '_');
          // If target type is string, add .Name to convert StringVariableElement to string
          const suffix = (mappedType === 'string') ? '.Name' : '';
          const variableReference = `Variables.${safeTypeName}.${safeElementName}${suffix}`;
          console.log(`  ✅ Generated Variable reference: ${variableReference}`);
          return variableReference;
        }
      } else if (field.referenceType === 'tags') {
        // For tags, extract the value as a string
        if (Array.isArray(field.referenceData)) {
          // Array of references - extract value from each
          const values = field.referenceData
            .map(refObj => {
              const val = refObj.value || refObj.code || refObj.name || refObj.id;
              return `"${String(val).replace(/"/g, '\\"')}"`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of tag values: new[] { ${values} }`);
          return `new[] { ${values} }`;
        } else {
          // Single reference - extract value
          const val = field.referenceData.value || field.referenceData.code || field.referenceData.name || field.referenceData.id;
          const stringValue = `"${String(val).replace(/"/g, '\\"')}"`;
          console.log(`  ✅ Generated tag value: ${stringValue}`);
          return stringValue;
        }
      } else if (field.referenceType === 'glossaryItem') {
        // For glossaryItem, add a TODO comment for now
        const itemName = field.referenceData.name || field.referenceData.id || 'Unknown';
        const safeItemName = itemName.replace(/[^a-zA-Z0-9_]/g, '_');
        console.log(`  ⚠️ GlossaryItem reference: ${safeItemName} - using string for now`);
        return `"${safeItemName}" /* TODO: Reference to glossaryItem */`;
      } else if (field.referenceType === 'dialog') {
        // Dialog references - store dialog key as string
        if (Array.isArray(field.referenceData)) {
          const dialogKeys = field.referenceData
            .map(refObj => {
              const dialogKey = refObj.dialog_key || refObj.name || refObj.id;
              return `"${String(dialogKey).replace(/"/g, '\\"')}"`;
            })
            .join(', ');
          console.log(`  ✅ Generated array of dialog keys: new[] { ${dialogKeys} }`);
          return `new[] { ${dialogKeys} }`;
        } else {
          const dialogKey = field.referenceData.dialog_key || field.referenceData.name || field.referenceData.id;
          const stringValue = `"${String(dialogKey).replace(/"/g, '\\"')}"`;
          console.log(`  ✅ Generated dialog key: ${stringValue}`);
          return stringValue;
        }
      } else if (field.referenceType === 'document') {
        // Document references - extract blocks from document data
        const document = field.referenceData;
        if (!document || !document.data) {
          console.log(`  ⚠️ Document has no data, returning empty array`);
          return 'new DocumentBlock[] { }';
        }
        
        // Document data is stored as DocumentData[] (array of sections)
        const blocks: Array<{ type: string; content: string; index: number }> = [];
        let currentIndex = 0;
        
        const documentData = Array.isArray(document.data) ? document.data : [document.data];
        
        documentData.forEach((section: any) => {
          if (section.blocks && Array.isArray(section.blocks)) {
            section.blocks.forEach((block: any) => {
              if (block.type === 'text' && block.content && block.content.trim()) {
                // Strip HTML tags from text content for clean export
                const cleanContent = stripHtmlTags(block.content);
                if (cleanContent.trim()) {
                  blocks.push({
                    type: 'text',
                    content: cleanContent,
                    index: currentIndex++
                  });
                }
              } else if (block.type === 'image' && block.metadata?.src) {
                blocks.push({
                  type: 'image',
                  content: block.metadata.src,
                  index: currentIndex++
                });
              }
            });
          }
        });
        
        if (blocks.length === 0) {
          console.log(`  ✅ Document has no blocks, returning empty array`);
          return 'new DocumentBlock[] { }';
        }
        
        // Format blocks as C# array
        const blockStrings = blocks.map(block => {
          const escapedContent = block.content
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\"')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r');
          return `new DocumentBlock { type = "${block.type}", content = "${escapedContent}", index = ${block.index} }`;
        });
        
        console.log(`  ✅ Generated ${blocks.length} document blocks`);
        return `new DocumentBlock[] { ${blockStrings.join(', ')} }`;
      }
    }
    
    if (value === null || value === undefined || value === '') {
      if (mappedType.includes('bool')) return 'false';
      if (mappedType.includes('int') || mappedType.includes('float') || mappedType.includes('double')) return '0';
      if (mappedType.includes('[]') || mappedType.includes('Array')) return 'new string[] { }';
      return '""';
    }
    
    if (Array.isArray(value)) {
      const arrItems = value.map(v => formatValueCSharp(v, 'string')).join(', ');
      return `new string[] { ${arrItems} }`;
    }
    
    if (typeof value === 'string') {
      const escaped = value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      return `"${escaped}"`;
    }
    
    if (typeof value === 'number') {
      if (mappedType.includes('float')) return `${value}f`;
      // If mapped as int but value has decimals, format as double
      if (mappedType.includes('int') && !Number.isInteger(value)) return value.toString();
      return value.toString();
    }
    
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    
    return `"${String(value)}"`;
  };
  
  // Recursive function to render nested C# classes
  const renderCSharpNode = (node: FolderNode | ItemFieldsNode, indent: number = 2): string => {
    const tabs = '\t'.repeat(indent);
    let output = '';
    
    for (const key of Object.keys(node)) {
      // Skip internal properties
      if (key.startsWith('_')) continue;
      
      const value = node[key];
      // Sanitize key for valid C# identifier
      const safeKey = key.replace(/[^a-zA-Z0-9_]/g, '_');
      
      if (value && typeof value === 'object' && '_isItem' in value) {
        // This is an item node - render as static class with fields
        const itemNode = value as ItemFieldsNode;
        output += `${tabs}public static class ${safeKey}\n${tabs}{\n`;
        
        itemNode._fields.forEach(field => {
          let fieldName = applyNamingConvention(field.label, fieldConvention);
          
          // Check for field name override (only if not using a global override)
          if (!namingConventionOverride && template.fieldNameOverrides && itemNode._itemData.blueprint_id) {
            const blueprintOverrides = template.fieldNameOverrides[itemNode._itemData.blueprint_id];
            if (blueprintOverrides && blueprintOverrides[field.label]) {
              const override = blueprintOverrides[field.label];
              if (override.useExportName && override.exportName) {
                fieldName = override.exportName;
              }
            }
          }
          
          // Sanitize field name for valid C# identifier
          const safeFieldName = fieldName.replace(/[^a-zA-Z0-9_]/g, '_');
          const actualValue = field.value !== undefined ? field.value : field.defaultValue;
          
          let fieldValue: string;
          let csharpType: string;
          
          // Check if this field is a reference field (types/tags)
          if (field.isReference && field.referenceData && field.referenceType) {
            csharpType = getCSharpType(field.mappedType, actualValue, false, field);
            fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
          }
          // Check if this field has string_ids (translatable field)
          else if (field.string_ids && field.string_ids.length > 0) {
            if (Array.isArray(actualValue) && actualValue.length === field.string_ids.length) {
              // For arrays, create array of translation key references
              const keyRefs = field.string_ids.map(stringId => {
                const stringItem = stringsMap.get(stringId);
                if (stringItem) {
                  const keyName = stringItem.abbrev || `key_${stringItem.id}`;
                  const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
                  return `TranslationKeys.${safeKeyName}`;
                }
                return null;
              }).filter(Boolean).join(', ');
              
              fieldValue = `new TranslationKey[] { ${keyRefs} }`;
              csharpType = 'TranslationKey[]';
            } else if (field.string_ids.length === 1) {
              // Single value
              const stringItem = stringsMap.get(field.string_ids[0]);
              if (stringItem) {
                const keyName = stringItem.abbrev || `key_${stringItem.id}`;
                const safeKeyName = keyName.replace(/[^a-zA-Z0-9_]/g, '_');
                fieldValue = `TranslationKeys.${safeKeyName}`;
                csharpType = 'TranslationKey';
              } else {
                csharpType = getCSharpType(field.mappedType, actualValue, false, field);
                fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
              }
            } else {
              // Fallback to regular value
              csharpType = getCSharpType(field.mappedType, actualValue, false, field);
              fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
            }
          } else {
            // Not translatable or reference, use the regular value
            csharpType = getCSharpType(field.mappedType, actualValue, false, field);
            fieldValue = formatValueCSharp(actualValue, field.mappedType, field);
          }
          
          output += `${tabs}\tpublic static readonly ${csharpType} ${safeFieldName} = ${fieldValue};\n`;
        });
        
        output += `${tabs}}\n\n`;
      } else if (value && typeof value === 'object') {
        // This is a folder node - render as nested static class
        output += `${tabs}public static class ${safeKey}\n${tabs}{\n`;
        output += renderCSharpNode(value as FolderNode, indent + 1);
        output += `${tabs}}\n\n`;
      }
    }
    
    return output;
  };
  
  let output = `// Auto-generated ${className} (Folder Structure)\n`;
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += `// Access data like: ${className}.Library.scenes.player_house.bed.inspect_text\n`;
  output += `//                 ${className}.Dialogs.quest_dialog.section_welcome.field_0_dialog_text\n\n`;
  output += 'using System;\n\n';
  output += `namespace ${namespace}\n{\n`;
  output += `\tpublic static class ${className}\n\t{\n`;
  
  // DocumentBlock class for document field types
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Represents a single block of content from a document\n';
  output += '\t\t/// </summary>\n';
  output += '\t\t[Serializable]\n';
  output += '\t\tpublic class DocumentBlock\n\t\t{\n';
  output += '\t\t\tpublic string type;     // "text" or "image"\n';
  output += '\t\t\tpublic string content;  // Text content or image URL\n';
  output += '\t\t\tpublic int index;       // Order in the document\n';
  output += '\t\t}\n\n';
  
  // Render Library section
  output += '\t\tpublic static class Library\n\t\t{\n';
  // Indent the library structure by one more level
  const libraryStructureLines = renderCSharpNode(structure).split('\n');
  const indentedLibraryStructure = libraryStructureLines.map(line => {
    if (line.trim()) return '\t' + line;
    return line;
  }).join('\n');
  output += indentedLibraryStructure;
  output += '\t\t}\n\n';
  
  // Render Dialogs section (if dialogs are provided and render function is available)
  if (dialogs && dialogs.length > 0 && renderDialogsDatabaseTemplateFn) {
    output += renderDialogsDatabaseTemplateFn(dialogs, folderPathResolver, stringsMap, fieldConvention);
  } else {
    output += '\t\tpublic static class Dialogs\n\t\t{\n\t\t\t// No dialogs exported\n\t\t}\n\n';
  }
  
  // Add unified Get<T> method that routes to both Library and DialogDatabase
  if (dialogs && dialogs.length > 0) {
    output += '\t\t/// <summary>\n';
    output += '\t\t/// Unified Get method that can access both Library items and Dialog sections.\n';
    output += '\t\t/// For dialog sections, use the format: "dialogKey/sectionId"\n';
    output += '\t\t/// For library items, use the folder path format: "folder/subfolder/itemName"\n';
    output += '\t\t/// </summary>\n';
    output += '\t\t/// <typeparam name="T">The type to cast the result to</typeparam>\n';
    output += '\t\t/// <param name="path">The path to the item or dialog section</param>\n';
    output += '\t\t/// <returns>The item cast to type T, or null if not found or wrong type</returns>\n';
    output += '\t\tpublic static T Get<T>(string path) where T : class\n\t\t{\n';
    output += '\t\t\tif (string.IsNullOrEmpty(path)) return null;\n\n';
    output += '\t\t\t// First try DialogDatabase.Dialogs if T is a DialogSection\n';
    output += '\t\t\tif (typeof(T).IsSubclassOf(typeof(DialogSection)) || typeof(T) == typeof(DialogSection))\n\t\t\t{\n';
    output += '\t\t\t\tif (DialogDatabase.Dialogs.TryGetValue(path, out var dialogSection))\n\t\t\t\t{\n';
    output += '\t\t\t\t\treturn dialogSection as T;\n';
    output += '\t\t\t\t}\n';
    output += '\t\t\t}\n\n';
    output += '\t\t\t// Fallback: try Library (note: Library uses nested structure, not dictionary in this template)\n';
    output += '\t\t\t// Library access in this template uses static nested classes, not runtime lookup\n';
    output += '\t\t\t// For Library items, use the static class path directly\n\n';
    output += '\t\t\treturn null;\n';
    output += '\t\t}\n\n';
  }
  
  output += '\t}\n}\n';
  
  console.log('✅ Folder-based C# template rendered successfully');
  
  return output;
}
